import { FunctionComponent, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import styles from './MultipleChoice.module.css';

/**
 * TODO: Add a way to add a custom option to the list that can have validation rules
 *    - For example "None of these" should be a valid option, but if the user selects it,
 *      none of the other options should be selected.
 */

interface Option {
  value: string;
  label: string;
  category?: string;
}

interface MultipleChoiceProps {
  id: string;
  label: string;
  options: Option[];
  values: string[];
  onChange: (values: string[]) => void;
  hasNextButton?: boolean;
  onNext?: () => void;
  searchable?: boolean;
  expandable?: boolean;
  required?: boolean;
  helperText?: string;
  error?: string;
  maxSelections?: number;
}

const MultipleChoice: FunctionComponent<MultipleChoiceProps> = ({
  id,
  label,
  options,
  values,
  onChange,
  hasNextButton = false,
  onNext,
  searchable = false,
  expandable = false,
  required = false,
  helperText,
  error,
  maxSelections
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(!expandable);

  const filteredOptions = searchable && searchQuery
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.category?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const groupedOptions = filteredOptions.reduce((acc, option) => {
    const category = option.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(option);
    return acc;
  }, {} as Record<string, Option[]>);

  const handleToggle = (value: string) => {
    const newValues = values.includes(value)
      ? values.filter(v => v !== value)
      : maxSelections && values.length >= maxSelections
        ? values
        : [...values, value];
    onChange(newValues);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
          {maxSelections && (
            <span className={styles.maxSelections}>
              ({values.length}/{maxSelections} selected)
            </span>
          )}
        </label>
        {expandable && !isExpanded && !hasNextButton && (
          <button
            type="button"
            className={styles.expandButton}
            onClick={() => setIsExpanded(true)}
          >
            Select options
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {searchable && (
            <div className={styles.searchContainer}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search options..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={clearSearch}
                >
                  <ClearIcon />
                </button>
              )}
            </div>
          )}

          <div className={styles.optionsContainer}>
            {Object.entries(groupedOptions).map(([category, categoryOptions]) => (
              <div key={category} className={styles.categoryGroup}>
                {Object.keys(groupedOptions).length > 1 && (
                  <h4 className={styles.categoryTitle}>{category}</h4>
                )}
                <div className={styles.optionsList}>
                  {categoryOptions.map((option) => (
                    <label key={option.value} className={styles.option}>
                      <input
                        type="checkbox"
                        id={`${id}-${option.value}`}
                        value={option.value}
                        checked={values.includes(option.value)}
                        onChange={() => handleToggle(option.value)}
                        disabled={!!maxSelections && values.length >= maxSelections && !values.includes(option.value)}
                        className={styles.checkbox}
                      />
                      <span className={styles.checkmark}></span>
                      <span className={styles.optionLabel}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {values.length > 0 && (
            <div className={styles.selectedChips}>
              {values.map((value) => {
                const option = options.find(o => o.value === value);
                return option ? (
                  <div key={value} className={styles.chip}>
                    <span>{option.label}</span>
                    <button
                      type="button"
                      className={styles.chipRemove}
                      onClick={() => handleToggle(value)}
                    >
                      <ClearIcon />
                    </button>
                  </div>
                ) : null;
              })}
            </div>
          )}

          {hasNextButton && onNext && (
            <div className={styles.nextButtonContainer}>
              <button
                type="button"
                className={styles.nextButton}
                onClick={onNext}
                disabled={required && values.length === 0}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {helperText && <p className={styles.helperText}>{helperText}</p>}
      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
};

export default MultipleChoice;